import type { ReactNode, Ref } from 'react'
import { useId } from 'react'
import './question.css'

/**
 * One question in a flow that asks them one at a time, as a region the surface
 * can hand focus to.
 *
 * Answering unmounts the control that was focused, which drops focus to the
 * body: the next key press starts again at the top of the document, and a
 * reader who cannot see the card is told nothing at all — on a surface whose
 * whole shape is one question replaced by the next. Focusing the region
 * announces the new question and puts the keyboard inside it.
 *
 * Layout only. It draws nothing the design system does not already own, and the
 * controls inside it are the shared primitives.
 *
 * Where the flow IS the page — a public payment — the question is its heading,
 * shown once in the serif, and the controls under it do not show it again. Inside a form it stays the region's name alone, and the
 * controls carry the question as their labels.
 */
export function Question({
  ask,
  ref,
  appearance = 'form',
  children,
}: {
  /** What this region is asking, as its accessible name. */
  ask: string
  ref?: Ref<HTMLDivElement>
  /** `page` shows the question as the region's heading. */
  appearance?: 'form' | 'page'
  children: ReactNode
}) {
  const id = useId()
  const titled = appearance === 'page'
  return (
    <div
      className="question"
      data-appearance={appearance}
      role="group"
      aria-label={titled ? undefined : ask}
      aria-labelledby={titled ? id : undefined}
      tabIndex={-1}
      ref={ref}
    >
      {titled ? (
        <h2 className="question__title" id={id}>
          {ask}
        </h2>
      ) : null}
      {children}
    </div>
  )
}

/**
 * The question itself, where the region holds more than one kind of answer.
 *
 * Set like a field label, because it introduces controls rather than reading as
 * prose. A region whose only answer is a `Choice` or a `BranchList` lets that
 * control carry the question instead, so it is never asked twice.
 */
export function Ask({ children }: { children: ReactNode }) {
  return <p className="question__ask">{children}</p>
}
